import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Search, LogOut, LayoutDashboard, Store, ShieldCheck, ChevronDown, Handshake, Wallet, Sun, Moon, Menu, X, Gift } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";
import { getHostClass } from "@/lib/host";
import { isInternalRole } from "@/lib/team";
import { DemoCta } from "@/components/demo/DemoCta";

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      onClick={toggle}
      className="pf-btn-glass p-2 rounded-full inline-flex items-center justify-center hover:scale-105 transition-transform"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="theme-toggle"
    >
      {isDark ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4 text-slate-700" />}
    </button>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const role = user && user.role;
  const isAdminUser = isInternalRole(role);
  const host = getHostClass();
  // B2B main site: strictly Pricing + About in the header, plus Login/Signup on the right.
  // Shopper surfaces (Coupons/Stores/Categories/Flash + search) only render on the app PWA.
  const isB2B = host === "main";
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="glass-nav sticky top-0 z-40" data-testid="site-navbar">
      <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center gap-8">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <img src="/cyannect-logo.png" alt="Cyannect — Marketing Mesh" className="h-12 w-auto -my-1" />
        </Link>

        {isAdminUser ? null : isB2B ? (
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600" data-testid="nav-b2b">
            <Link to="/shoppers" className="hover:text-slate-900 transition-colors" data-testid="nav-shoppers">For shoppers</Link>
            <Link to="/pricing" className="hover:text-slate-900 transition-colors" data-testid="nav-pricing">Pricing</Link>
            <Link to="/about" className="hover:text-slate-900 transition-colors" data-testid="nav-about">About</Link>
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <Link to="/coupons" className="hover:text-slate-900 transition-colors" data-testid="nav-coupons">Coupons</Link>
            <Link to="/shoppers" className="hover:text-slate-900 transition-colors" data-testid="nav-shoppers">For shoppers</Link>
            <Link to="/why-cyannect" className="hover:text-slate-900 transition-colors" data-testid="nav-why">Why Cyannect</Link>
            <Link to="/about" className="hover:text-slate-900 transition-colors" data-testid="nav-about">About</Link>
            <Link to="/coupons?flash=true" className="hover:text-slate-900 transition-colors flex items-center gap-1.5" data-testid="nav-flash">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" /> Flash
            </Link>
          </nav>
        )}

        {!isB2B && (
          <form onSubmit={submit} className="flex-1 max-w-sm hidden md:block">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search coupons, stores…"
                className="w-full pl-9 pr-4 py-2 pf-input text-sm text-slate-900 placeholder-slate-400"
                data-testid="nav-search-input"
              />
            </div>
          </form>
        )}

        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          {isB2B && !user && (
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden pf-btn-glass p-2 rounded-full"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          )}
          {!user ? (
            <>
              <Link to="/login" className="text-sm font-medium px-3 py-2 rounded-full text-slate-700 hover:text-slate-900 transition-colors" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="pf-btn-primary text-sm px-4 py-2 rounded-full" data-testid="nav-register">{isB2B ? "Get Started" : "Sign up"}</Link>
              <DemoCta testid="nav-schedule-demo" className="hidden lg:inline-flex text-sm font-medium px-4 py-2 rounded-full border border-slate-300 text-slate-800 hover:border-slate-900 transition-colors" />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <NotificationBell />
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 px-3 py-2 rounded-full pf-btn-glass text-sm" data-testid="nav-user-menu">
                <span className="w-6 h-6 rounded-full pf-gradient-bg flex items-center justify-center text-xs font-semibold text-white">
                  {(user.name || user.email)[0].toUpperCase()}
                </span>
                <span className="hidden md:inline">{user.name}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 glass-strong rounded-2xl py-2" onClick={() => setMenuOpen(false)}>
                  {role === "user" && (
                    <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1" data-testid="nav-user-dashboard"><LayoutDashboard className="w-4 h-4" /> Dashboard</Link>
                  )}
                  {role === "merchant" && (
                    <Link to="/merchant" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1" data-testid="nav-merchant-portal"><Store className="w-4 h-4" /> Merchant portal</Link>
                  )}
                  {isAdminUser && (
                    <Link to="/admin" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1" data-testid="nav-admin-portal"><ShieldCheck className="w-4 h-4" /> Admin console</Link>
                  )}
                  {role !== "merchant" && (<>
                    <Link to="/affiliate" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1" data-testid="nav-affiliate-portal"><Handshake className="w-4 h-4" /> Affiliate program</Link>
                    <Link to="/wallet" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1" data-testid="nav-wallet"><Wallet className="w-4 h-4" /> Cashback wallet</Link>
                  </>)}
                  {role === "user" && (
                    <Link to="/rewards" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1" data-testid="nav-rewards"><Gift className="w-4 h-4" /> Rewards & gift cards</Link>
                  )}
                  <div className="border-t border-white/50 my-1" />
                  <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/50 rounded-lg mx-1 text-left" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                </div>
              )}
            </div>
            </div>
          )}
        </div>
      </div>
      {isB2B && mobileOpen && (
        <div className="lg:hidden border-t border-white/40 bg-white/70 backdrop-blur" data-testid="nav-b2b-mobile">
          <div className="max-w-[1400px] mx-auto px-6 py-3 flex flex-col gap-1 text-sm font-medium text-slate-700">
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-lg hover:bg-white/70" data-testid="nav-pricing-mobile">Pricing</Link>
            <Link to="/shoppers" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-lg hover:bg-white/70" data-testid="nav-shoppers-mobile">For shoppers</Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-lg hover:bg-white/70" data-testid="nav-about-mobile">About</Link>
            <div onClick={() => setMobileOpen(false)}><DemoCta testid="nav-schedule-demo-mobile" className="block px-3 py-2 rounded-lg hover:bg-white/70" /></div>
          </div>
        </div>
      )}
    </header>
  );
}
